import { useCallback, useEffect, useState } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";

import { useAuth } from "../services/AuthContext";
import {
  createConversation,
  fetchAIContext,
  listConversations,
} from "./api";
import ConversationView from "./ConversationView";
import ContractsPanel from "./ContractsPanel";
import DealActionsPanel from "./DealActionsPanel";
import DealSituationPanel from "./DealSituationPanel";
import NotificationsPanel from "./NotificationsPanel";
import SellerWizard from "./SellerWizard";

// Two-pane shell:
//   left  → sidebar (brand, conversation list, "Ny konversation" button, user + logout)
//   right → active conversation (via :id) OR the empty-state prompt
export default function AIWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const [context, setContext] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [contextError, setContextError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState("");

  // Load context + conversation list in parallel on mount
  useEffect(() => {
    const controller = new AbortController();
    let alive = true;
    (async () => {
      try {
        const [ctx, convs] = await Promise.all([
          fetchAIContext({ signal: controller.signal }),
          listConversations({ signal: controller.signal }),
        ]);
        if (!alive) return;
        setContext(ctx);
        setConversations(convs || []);
      } catch (e) {
        if (!alive || e.name === "AbortError") return;
        setContextError(e?.body?.detail || e.message || "Kunde inte ladda kontext");
      }
    })();
    return () => {
      alive = false;
      controller.abort();
    };
  }, []);

  const refreshConversations = useCallback(async () => {
    try {
      const convs = await listConversations();
      setConversations(convs || []);
    } catch {
      /* non-fatal — sidebar stays as-is */
    }
  }, []);

  const startNewConversation = useCallback(
    async ({ initialMessage } = {}) => {
      if (creating) return;
      setCreating(true);
      try {
        const conv = await createConversation({
          workspaceId: context?.active_workspace?.id ?? null,
        });
        // Refresh sidebar so the new (empty) conversation shows up.
        setConversations((prev) => [
          { id: conv.id, title: conv.title, updated_at: conv.updated_at, workspace: conv.workspace },
          ...prev,
        ]);
        navigate(`/c/${conv.id}`, {
          state: initialMessage ? { initialUserMessage: initialMessage } : undefined,
        });
      } catch (e) {
        setContextError(e?.body?.detail || e.message || "Kunde inte skapa konversation");
      } finally {
        setCreating(false);
      }
    },
    [creating, context, navigate]
  );

  const handleHomeSubmit = async (e) => {
    e.preventDefault();
    const content = draft.trim();
    if (!content || creating) return;
    setDraft("");
    await startNewConversation({ initialMessage: content });
  };

  const displayName =
    context?.user?.full_name || context?.user?.email || "";
  const activeCompany = context?.companies?.[0]?.name || "";
  const activeDeal = context?.active_workspace?.organisation_name;

  // ── Compact intelligence chips (step 4) ──
  // Both objects come from the /ai/context/ endpoint's new
  // {company_intelligence, deal_intelligence} keys. Server-side
  // authorization already gated them by session; the frontend is
  // read-only.
  const companyIntel = context?.company_intelligence;
  const dealIntel = context?.deal_intelligence;
  const targetCompanyFact = companyIntel?.sections?.identity?.target_company_name;
  const dealStageFact = dealIntel?.sections?.process?.deal_stage;
  const revenueFact = companyIntel?.sections?.financials?.revenue;
  const fiscalYearFact = companyIntel?.sections?.financials?.fiscal_year;
  const valuationMidFact = dealIntel?.sections?.valuation?.equity_value_mid;

  return (
    <div className="qai-shell qai-shell-split">
      <aside className="qai-sidebar">
        <div className="qai-sidebar-brand">
          <span className="qai-brand-mark">Q</span>
          <span>Qada AI</span>
        </div>

        {(targetCompanyFact?.known || dealStageFact?.known) && (
          <div
            className="qai-sidebar-context"
            title="Vad Qada vet om affären just nu"
          >
            {targetCompanyFact?.known && (
              <div className="qai-sc-row">
                <span className="qai-sc-label">Målbolag</span>
                <span className="qai-sc-value">
                  {targetCompanyFact.value}
                </span>
              </div>
            )}
            {dealStageFact?.known && (
              <div className="qai-sc-row">
                <span className="qai-sc-label">Steg</span>
                <span className="qai-sc-value qai-sc-value-badge">
                  {dealStageFact.value}
                </span>
              </div>
            )}
            {revenueFact?.known && (
              <div className="qai-sc-row">
                <span className="qai-sc-label">Omsättning</span>
                <span className="qai-sc-value">
                  {formatMoneyShort(revenueFact.value)}
                  {revenueFact.unit ? ` ${revenueFact.unit}` : ""}
                  {fiscalYearFact?.known ? ` · ${fiscalYearFact.value}` : ""}
                </span>
              </div>
            )}
            {valuationMidFact?.known && (
              <div className="qai-sc-row">
                <span className="qai-sc-label">Värdering</span>
                <span className="qai-sc-value">
                  ~{formatMoneyShort(valuationMidFact.value)}
                  {valuationMidFact.unit ? ` ${valuationMidFact.unit}` : ""}
                </span>
              </div>
            )}
          </div>
        )}

        <button
          type="button"
          className="qai-new-conv"
          onClick={() => startNewConversation()}
          disabled={creating}
        >
          + Ny konversation
        </button>

        <div className="qai-conv-list">
          {conversations.length === 0 && (
            <div className="qai-conv-list-empty">Inga konversationer än.</div>
          )}
          {conversations.map((c) => (
            <NavLink
              key={c.id}
              to={`/c/${c.id}`}
              className={({ isActive }) =>
                "qai-conv-item" + (isActive ? " qai-conv-item-active" : "")
              }
            >
              <span className="qai-conv-title">{c.title || "Konversation"}</span>
            </NavLink>
          ))}
        </div>

        <div className="qai-sidebar-footer">
          {displayName && (
            <div className="qai-sidebar-user">
              <div className="qai-sidebar-user-name">{displayName}</div>
              {activeCompany && (
                <div className="qai-sidebar-user-sub">{activeCompany}</div>
              )}
            </div>
          )}
          <button type="button" className="qai-logout" onClick={logout}>
            Logga ut
          </button>
        </div>
      </aside>

      <main className="qai-main-split">
        {contextError && <div className="qai-error">{contextError}</div>}

        {id ? (
          <ConversationView
            // Force remount on :id change so all local state resets
            key={id}
            onConversationChanged={refreshConversations}
          />
        ) : (
          <div className="qai-home">
            <div className="qai-home-inner">
              <div className="qai-hero">
                <h1>Vad vill du göra idag?</h1>
                <p>
                  {activeDeal
                    ? `Fråga Qada AI om ${activeDeal} eller om försäljningsprocessen.`
                    : "Fråga Qada AI om värdering, köpare eller processen."}
                </p>
              </div>

              <form className="qai-input-row qai-home-input" onSubmit={handleHomeSubmit}>
                <textarea
                  className="qai-input"
                  placeholder="Ask Qada — t.ex. Vad är mitt bolag värt?"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleHomeSubmit(e);
                    }
                  }}
                  rows={2}
                  disabled={creating}
                />
                <button
                  type="submit"
                  className="qai-send"
                  disabled={creating || !draft.trim()}
                >
                  {creating ? "…" : "Skicka"}
                </button>
              </form>

              <div className="qai-placeholder-note">
                AI Deal Agent svarar direkt. Handlingsverktyg — skicka mejl,
                uppdatera värdering, hitta köpare — kommer i senare release.
              </div>

              {context?.active_workspace?.id && (
                <>
                  {/* Vägledning för säljare — sitter överst så steg 1
                      (preliminär värdering) är det första man ser. */}
                  <SellerWizard
                    workspaceId={context.active_workspace.id}
                    onSiggePrompt={(prompt) =>
                      startNewConversation({ initialMessage: prompt })
                    }
                  />
                  <NotificationsPanel workspaceId={context.active_workspace.id} />
                  <DealSituationPanel workspaceId={context.active_workspace.id} />
                  <DealActionsPanel workspaceId={context.active_workspace.id} />
                  <ContractsPanel workspaceId={context.active_workspace.id} />
                </>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

// Short money format: 1 234 567 → "1,2M". Kept intentionally lossy —
// the sidebar chips are glance-value, the full number is in the AI
// context (and available via the get_company / get_current_deal tools).
function formatMoneyShort(v) {
  if (typeof v !== "number" || !isFinite(v)) return String(v ?? "");
  const abs = Math.abs(v);
  if (abs >= 1_000_000_000) return (v / 1_000_000_000).toFixed(1).replace(".0", "") + "M";
  if (abs >= 1_000_000) return (v / 1_000_000).toFixed(1).replace(".0", "") + "M";
  if (abs >= 1_000) return (v / 1_000).toFixed(0) + "k";
  return String(v);
}
